import { useState } from 'react';
import { motion } from 'framer-motion';
import { Diamond, Mail, Send, ExternalLink, Instagram, Gift, Sparkles, ChevronDown } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    reason: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactReasons = [
    'Casino Partnership',
    'Product & Service Sponsorship',
    'Giveaway Winner Follow Up',
    'Stream/Content Topic Requests',
    'AI Business Consultation'
  ];

  const affiliates = [
    {
      name: 'Legendz Casino',
      description: 'Join Legendz Casino with my exclusive link and start your gaming adventure!',
      image: 'https://customer-assets.emergentagent.com/job_ugc-marketing-brand/artifacts/5ozl958l_ChatGPT%20Image%20Dec%2018%2C%202025%2C%2005_36_06%20PM.png',
      link: 'https://www.legendz.com/en-US/?register=me&btag=59_37ga1319cidpidvar1var2var3var4var5affid59tid&promo=SHAYLA',
      color: '#9D4EDD',
      promo: 'Use code: SHAYLA'
    },
    {
      name: 'PlayFame',
      description: 'Check out my profile on PlayFame and join the fun!',
      image: 'https://customer-assets.emergentagent.com/job_ugc-marketing-brand/artifacts/vy6scncj_52e601cad9acb2b998aaf11a64b567c2_0.jpeg',
      link: 'https://www.playfame.com/@relatableshayla',
      color: '#39FF14',
      promo: 'Follow @RelatableShayla'
    }
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleReasonChange = (value) => {
    setFormData({ ...formData, reason: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! I\'ll get back to you soon.');
      setFormData({ name: '', email: '', company: '', reason: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again or reach out on Instagram.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="contact-page">
      {/* Decorative blobs */}
      <div className="glow-blob glow-blob-purple top-40 -left-48 fixed" />
      <div className="glow-blob glow-blob-green bottom-40 -right-48 fixed" />

      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <motion.section
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="flex items-center justify-center gap-2 mb-4">
            <Diamond className="w-5 h-5 text-[#39FF14] sparkle" />
            <span className="text-[#39FF14] font-medium uppercase tracking-wider text-sm">Get In Touch</span>
            <Diamond className="w-5 h-5 text-[#39FF14] sparkle" />
          </motion.div>

          <motion.h1 
            variants={itemVariants}
            className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase mb-6"
          >
            <span className="text-[#E0E0E0]">Let's</span>{' '}
            <span className="text-glow-purple text-[#9D4EDD]">Connect</span>
          </motion.h1>

          <motion.p 
            variants={itemVariants}
            className="text-lg text-gray-400 max-w-2xl mx-auto"
          >
            Interested in collaborating? Have a partnership opportunity? 
            I'd love to hear from you! Fill out the form or reach out on social media.
          </motion.p>
        </motion.section>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.section
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div variants={itemVariants} className="glass-card p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#9D4EDD]/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#9D4EDD]" />
                </div>
                <h2 className="text-xl font-bold text-[#E0E0E0]">Business Inquiries</h2>
              </div>

              <form onSubmit={handleSubmit} className="contact-form space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Name *</label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="bg-[#141414] border-white/10 focus:border-[#9D4EDD]"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Email *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      className="bg-[#141414] border-white/10 focus:border-[#9D4EDD]"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Company</label>
                  <Input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your company (optional)"
                    className="bg-[#141414] border-white/10 focus:border-[#9D4EDD]"
                    data-testid="input-company"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Reason for Contact *</label>
                  <Select value={formData.reason} onValueChange={handleReasonChange} required>
                    <SelectTrigger 
                      className="bg-[#141414] border-white/10 focus:border-[#9D4EDD] text-[#E0E0E0]"
                      data-testid="select-reason"
                    >
                      <SelectValue placeholder="Select a reason..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      {contactReasons.map((reason) => (
                        <SelectItem 
                          key={reason} 
                          value={reason}
                          className="text-[#E0E0E0] focus:bg-[#9D4EDD]/20 focus:text-white"
                        >
                          {reason}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Message *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell me about your project or partnership opportunity..."
                    rows={5}
                    className="bg-[#141414] border-white/10 focus:border-[#9D4EDD] resize-none"
                    data-testid="input-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary py-3 rounded-full font-semibold flex items-center justify-center gap-2"
                  data-testid="submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>

              {/* Instagram CTA */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-gray-400 text-sm mb-4 text-center">Or reach out directly on Instagram</p>
                <a
                  href="https://www.instagram.com/relatableshayla?igsh=bnR1Z3ZsbzZkMnU2"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 text-[#E4405F] hover:text-[#E4405F]/80 transition-colors font-medium"
                  data-testid="instagram-link"
                >
                  <Instagram className="w-5 h-5" />
                  @RelatableShayla
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          </motion.section>

          {/* Affiliate Links */}
          <motion.section
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div variants={itemVariants} className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <Gift className="w-6 h-6 text-[#39FF14]" />
                <h2 className="text-2xl font-bold text-[#E0E0E0]">Partner Referrals</h2>
              </div>
              <p className="text-gray-400">
                Check out my trusted partners and join using my exclusive links!
              </p>
            </motion.div>

            <div className="space-y-6">
              {affiliates.map((affiliate, index) => (
                <motion.div
                  key={affiliate.name}
                  variants={itemVariants}
                  className="affiliate-card overflow-hidden"
                  data-testid={`affiliate-card-${index}`}
                >
                  <div className="flex flex-col sm:flex-row">
                    <div className="sm:w-1/3 aspect-square sm:aspect-auto">
                      <img
                        src={affiliate.image}
                        alt={affiliate.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-6 sm:w-2/3 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4" style={{ color: affiliate.color }} />
                          <h3 className="text-lg font-bold text-[#E0E0E0]">{affiliate.name}</h3>
                        </div>
                        <p className="text-gray-400 text-sm mb-4">{affiliate.description}</p>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-4"
                          style={{ backgroundColor: `${affiliate.color}20`, color: affiliate.color }}
                        >
                          <Gift className="w-3 h-3" />
                          {affiliate.promo}
                        </div>
                      </div>
                      <a
                        href={affiliate.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-ghost px-6 py-2 rounded-full font-semibold text-center flex items-center justify-center gap-2 text-sm"
                        style={{ borderColor: affiliate.color, color: affiliate.color }}
                        data-testid={`affiliate-link-${index}`}
                      >
                        Visit {affiliate.name}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Discord Card */}
            <motion.div 
              variants={itemVariants}
              className="mt-6 glass-card p-6"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#5865F2]/20 flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" className="w-7 h-7 text-[#5865F2]" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-[#E0E0E0] mb-1">Join The Relatable Family</h3>
                  <p className="text-gray-400 text-sm">Connect with the community on Discord</p>
                </div>
                <a
                  href="https://discord.gg/FEwaZfwnN5"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:flex discord-btn text-sm py-2 px-4"
                  data-testid="discord-link-contact"
                >
                  Join
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              <a
                href="https://discord.gg/FEwaZfwnN5"
                target="_blank"
                rel="noopener noreferrer"
                className="sm:hidden discord-btn text-sm py-2 px-4 mt-4 w-full justify-center"
                data-testid="discord-link-contact-mobile"
              >
                Join Discord
                <ExternalLink className="w-4 h-4" />
              </a>
            </motion.div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
